import React, { useLayoutEffect, useRef, useState } from 'react'

import {
  collectResumePreviewLineGuides,
  stripeStyleForResumeLineGuide,
  visualOffsetYToLocalGuideY,
  type ResumeLineGuide,
} from '../../utils/resumePreviewLineGuides'
import { useResumePreviewFitScale } from './resumePreviewFitScaleContext'

type ResumePreviewLineGuidesContentProps = {
  lineGuides?: boolean
  /** Limit stripe collection (e.g. `[data-paysafe-header]` in sheet-1 header band). */
  guideScopeSelector?: string
  children: React.ReactNode
}

/**
 * Wraps visible preview slice content; line stripes use the same layout tree as text.
 */
export default function ResumePreviewLineGuidesContent({
  lineGuides = false,
  guideScopeSelector,
  children,
}: ResumePreviewLineGuidesContentProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const fitScale = useResumePreviewFitScale()
  const [guides, setGuides] = useState<ResumeLineGuide[]>([])
  const [overlayHeight, setOverlayHeight] = useState(0)

  useLayoutEffect(() => {
    if (!lineGuides) {
      setGuides([])
      setOverlayHeight(0)
      return
    }
    const root = rootRef.current
    if (!root) return

    const update = () => {
      const el = rootRef.current
      if (!el) return
      setGuides(
        collectResumePreviewLineGuides(el, {
          scopeSelector: guideScopeSelector,
          coordinateScale: fitScale,
        })
      )
      const scopeEl =
        guideScopeSelector != null
          ? el.querySelector<HTMLElement>(guideScopeSelector)
          : null
      if (scopeEl) {
        const marker = scopeEl.querySelector<HTMLElement>('[data-line-guide-header-end]')
        const anchor = marker ?? scopeEl
        const rootTop = el.getBoundingClientRect().top
        setOverlayHeight(
          visualOffsetYToLocalGuideY(anchor.getBoundingClientRect().bottom - rootTop, fitScale)
        )
      } else {
        setOverlayHeight(el.scrollHeight)
      }
    }

    update()
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      void document.fonts.ready.then(update)
    }

    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(root)
    return () => ro.disconnect()
  }, [lineGuides, guideScopeSelector, children, fitScale])

  return (
    <div ref={rootRef} className="resume-preview-line-guides-root relative w-full min-w-0">
      {children}
      {lineGuides && guides.length > 0 && (
        <div
          className="resume-preview-line-guides pointer-events-none absolute inset-x-0 top-0 z-20"
          style={{ height: overlayHeight }}
          aria-hidden
          data-testid="resume-line-guides-layer"
        >
          {guides.map((g) => {
            const stripe = stripeStyleForResumeLineGuide(g)
            return (
              <div
                key={`${g.y}-${g.block}`}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: stripe.top,
                  height: stripe.height,
                  transform: `translateY(-${stripe.height}px)`,
                  backgroundColor: stripe.backgroundColor,
                  backgroundImage: stripe.backgroundImage,
                  boxShadow: stripe.boxShadow,
                  opacity: stripe.opacity,
                }}
                title={stripe.label ?? g.block}
              >
                {stripe.label && (
                  <span
                    className="absolute right-0 top-0 -translate-y-full rounded-sm bg-sky-600 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-white"
                    style={{ lineHeight: 1.1 }}
                  >
                    {stripe.label}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
